import React from "react";
import { compose } from "redux";
import { RouteComponentProps } from "react-router";
import { connect } from "react-redux";
import myInitObject from "../../../modules/GlobalConstants";
import { AppRoute } from "../../../modules/routing/constants";
// HOCs
import withUser, { WithUserProps } from "../../auth/hoc/withUser";
import withRecords, { WithRecordsProps } from "../hoc/withRecords";
import withInactivityDetection from "../hoc/withInactivityDetection";
import withRecordData, { WithRecordDataProps } from "../hoc/withRecordData";
import withWSConnection, {
  WithWSConnectionProps,
} from "../../ws/hoc/withWSConnection";

// Components
import RecordPage from "../components/RecordPageSwitch";

// Hooks
import usePrevious from "../../../hooks/usePrevious";

// Models
import { RecordTypeEnum } from "../models";
import { RecordStatusEnum } from "../../doctor-stats/models";
import { RootState } from "../../../models";

// Selectors
import { selectRecordConflict } from "../store/selectors";

// Utils
import {
  clearOpenRecordId,
  readOpenRecordId,
  recordFetchKind,
  writeOpenRecordId,
} from "../utils/openRecord";

interface LocationState {
  id?: string;
}

type RecordPageContainerProps = RouteComponentProps &
  WithUserProps &
  WithRecordsProps &
  WithWSConnectionProps &
  WithRecordDataProps & {
    recordConflict: boolean;
  };

/**
 * @param props
 */
function RecordPageContainer(props: RecordPageContainerProps) {
  const {
    fetchPDFRecord,
    fetchNextRecord,
    clearCurrentRecord,
    currentRecord,
    openSingleRecordWSConnection,
    closeSingleRecordWSConnection,
    location,
    history,
    fetchNextRecordById,
    fetchInProgressNextRecordById,
    recordConflict,
  } = props;
  const prevRecord = usePrevious(currentRecord);

  React.useEffect(() => {
    const resumeInProgress = myInitObject.FetchRecordStatus === "true";
    myInitObject.FetchRecordStatus = "";

    const routeState = location.state as LocationState | undefined;
    const recordId = routeState ? routeState.id : undefined;

    const kind = recordFetchKind({
      recordId,
      resumeInProgress,
      openedRecordId: readOpenRecordId(),
    });

    if (typeof recordId === "undefined" || kind === "next") {
      fetchNextRecord();
    } else if (kind === "resume") {
      fetchInProgressNextRecordById(recordId);
    } else {
      fetchNextRecordById(recordId);
    }

    writeOpenRecordId(recordId);

    return () => {
      clearOpenRecordId();
      clearCurrentRecord();
      closeSingleRecordWSConnection();
    };
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (recordChanged()) {
      if (currentRecord !== null) {
        if (currentRecord.status === RecordStatusEnum.APPROVED) {
          closeSingleRecordWSConnection();
          history.replace(AppRoute.Home);
        }
        openSingleRecordWSConnection();
        if (currentRecord.recordType === RecordTypeEnum.HOLTER) {
          fetchPDFRecord(currentRecord.teleRecordId);
        }
      } else {
        closeSingleRecordWSConnection();
      }
    }
    // eslint-disable-next-line
  }, [currentRecord]);
  /*
   * The single exit for "this record is no longer yours", whether that was
   * discovered by an HTTP conflict or pushed over the websocket as
   * `doctorUnassignedWSEvent`.
   *
   * It replaces a `setInterval(…, 1000)` that polled `myInitObject.data`, a
   * mutable module-level string the websocket service wrote to directly. That
   * flag was only ever cleared HERE, and only after it had already navigated
   * away — so an unassign that arrived while the doctor was on the dashboard
   * stayed set indefinitely and threw them out of the NEXT record they opened,
   * on mount, before they could read anything. `recordConflict` is redux state
   * reset by every successful record fetch and by `clearCurrentRecord`, so it
   * cannot outlive the record it refers to.
   */
  React.useEffect(() => {
    if (recordConflict) {
      closeSingleRecordWSConnection();
      history.replace(AppRoute.Home);
    }
  }, [recordConflict, closeSingleRecordWSConnection, history]);

  function recordChanged() {
    return typeof prevRecord !== "undefined" && prevRecord !== currentRecord;
  }

  return <RecordPage {...props} />;
}

const mapStateToProps = (state: RootState) => ({
  recordConflict: selectRecordConflict(state),
});

const WithConflictState = connect(mapStateToProps)(RecordPageContainer);

export default compose(
  withUser,
  withRecords,
  withRecordData,
  withWSConnection,
  withInactivityDetection
)(WithConflictState);
